import jsPDF from "jspdf";
import { addLexendFont } from "./lexendFont";

// ---- Meal plan code → full display label ----
const mealPlanLabel = (code) => {
  if (!code) return '-';
  const map = {
    'ep': 'No Meal',
    'cp': 'Breakfast',
    'map': 'Breakfast + Dinner',
    'ap': 'All Meals',
    'ai': 'All Meals',
    'apai': 'All Meals',
  };
  return map[code.toLowerCase()] || code.toUpperCase();
};

// ---- Local helpers (mirrored from QuoteDetail.js so the util is self-contained) ----
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  // Directly parse ISO YYYY-MM-DD strings to avoid timezone-shift artefacts
  // (new Date('YYYY-MM-DD') is UTC midnight — local .getDate() drifts for UTC-offset TZs)
  if (typeof dateStr === 'string') {
    const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  }
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return String(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '₹0';
  return `₹${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};
const parseDayDate = (dayDateStr, travelDate, dayIndex) => {
  if (dayDateStr) {
    const isoDate = new Date(dayDateStr + 'T00:00:00');
    if (!isNaN(isoDate.getTime())) return isoDate;
    const parsed = new Date(dayDateStr);
    if (!isNaN(parsed.getTime())) return parsed;
  }
  if (travelDate) {
    const base = new Date(travelDate + 'T00:00:00');
    if (!isNaN(base.getTime())) {
      const d = new Date(base); d.setDate(d.getDate() + dayIndex); return d;
    }
  }
  return null;
};
const formatDayDateLabel = (date) => {
  if (!date) return '';
  const dayAbbr = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  const dayOfMonth = String(date.getDate()).padStart(2, '0');
  const monthNum = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dayAbbr} - ${dayOfMonth}/${monthNum}/${yyyy}`;
};
const calculateNights = (travelDate, returnDate) => {
  if (!travelDate || !returnDate) return 0;
  const start = new Date(travelDate); const end = new Date(returnDate);
  return Math.max(0, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
};
const getHotelStarLabel = (category) => {
  if (!category) return '';
  return String(category).replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

/**
 * Generate the rich agent-style quote PDF.
 * @param {Object} quote — quote data from /api/quotes/:id
 * @param {Object} agent — { company_name, name, phone, email, address, city, state, website, logo_url, profile_picture_url, designation }
 * @param {Object} opts — { agentMarkup={adult,cwb,cnb}, markupMode='manual'|'percentage', markupPercentage=0 }
 * @returns {jsPDF|null} jsPDF doc instance (call .save(...) on it)
 */
export function generateQuotePdfDoc(quote, agent, opts = {}) {
  const agentMarkup = opts.agentMarkup || { adult: 0, cwb: 0, cnb: 0 };
  const markupMode = opts.markupMode || 'manual';
  const markupPercentage = Number(opts.markupPercentage) || 0;
  if (!quote) return null;

  // Derived state (mirrors QuoteDetail.js component-level computations)
  const accommodationOptions = quote.accommodation_options || [];
  const hasMultipleOptions = accommodationOptions.length > 1;
  const activeOption = hasMultipleOptions ? accommodationOptions[0] : (accommodationOptions[0] || quote);
  const getActiveOption = () => activeOption;
  const hotels = (Array.isArray(activeOption.hotels) && activeOption.hotels.length > 0)
    ? activeOption.hotels
    : (Array.isArray(quote.hotels) ? quote.hotels : []);
  const itinerary = (Array.isArray(quote.itinerary) && quote.itinerary.length > 0)
    ? quote.itinerary
    : (Array.isArray(activeOption.itinerary) ? activeOption.itinerary : []);
  const nights = quote.nights || calculateNights(quote.travel_date, quote.return_date);
  const getSellingPrice = (field) => {
    const basePrice = hasMultipleOptions ? (getActiveOption()[field] || 0) : (quote[field] || 0);
    if (markupMode === 'percentage') return basePrice + basePrice * (markupPercentage / 100);
    const m = { quote_per_adult: agentMarkup.adult, quote_per_cwb: agentMarkup.cwb, quote_per_cnb: agentMarkup.cnb };
    return basePrice + (m[field] || 0);
  };
  const getSellingTotal = () => {
    return (getSellingPrice('quote_per_adult') * (quote.pax_adults || 0)) +
           (getSellingPrice('quote_per_cwb') * (quote.cwb || 0)) +
           (getSellingPrice('quote_per_cnb') * (quote.cnb || 0));
  };
  // Agent-controlled add-on prices (Flight / Hotel / Visa) — shown as line items
  // in Cost Breakdown and as bullet points under Inclusions.
  const flightPrice = Number(quote.flight_price || 0);
  const hotelPrice = Number(quote.hotel_price || 0);
  const visaPrice = Number(quote.visa_price || 0);
  const addonsTotal = flightPrice + hotelPrice + visaPrice;

    const doc = new jsPDF('p', 'mm', 'a4');
    addLexendFont(doc);
    const pw = 210; // page width
    const mx = 18; // margin x
    const cw = pw - 2 * mx; // content width
    const teal = [16, 185, 129];
    const dark = [33, 33, 33];
    const grey = [120, 120, 120];
    const lightGrey = [180, 180, 180];
    const red = [220, 60, 60];
    let y = 18;

    const addPage = () => { doc.addPage(); y = 18; };
    const checkPage = (needed) => { if (y + needed > 280) addPage(); };
    const drawLine = (x1, y1, x2, y2, color = teal, w = 0.5) => {
      doc.setDrawColor(...color); doc.setLineWidth(w); doc.line(x1, y1, x2, y2);
    };
    const drawRect = (x, ry, w, h, fill, radius = 2) => {
      doc.setFillColor(...fill); doc.roundedRect(x, ry, w, h, radius, radius, 'F');
    };
    const setFont = (size, color = dark, style = 'normal') => {
      doc.setFontSize(size + 2); doc.setTextColor(...color); doc.setFont('Lexend', style);
    };
    const drawIcon = (x, iy, type) => {
      doc.setFillColor(...teal);
      if (type === 'circle') { doc.circle(x + 2, iy + 2, 2, 'F'); }
      else if (type === 'square') { doc.roundedRect(x, iy, 4, 4, 0.5, 0.5, 'F'); }
      else { doc.circle(x + 2, iy + 2, 2, 'F'); }
    };

    // ---- HEADER ----
    // Logo area (left)
    drawRect(mx, y, 30, 20, [240, 240, 240], 3);
    if (agent.logo_url) {
      try {
        // logo_url may be a base64 data URL (from agent upload) or a remote URL
        const ext = (agent.logo_url.startsWith('data:image/jpeg') || agent.logo_url.startsWith('data:image/jpg')) ? 'JPEG' : 'PNG';
        doc.addImage(agent.logo_url, ext, mx + 1, y + 1, 28, 18, undefined, 'FAST');
      } catch (err) {
        console.warn('PDF logo embed failed:', err);
        setFont(7, grey); doc.text('COMPANY', mx + 7, y + 9); doc.text('LOGO', mx + 10, y + 13);
      }
    } else {
      setFont(7, grey); doc.text('COMPANY', mx + 7, y + 9); doc.text('LOGO', mx + 10, y + 13);
    }

    // Company details (right)
    const rx = mx + 40;
    const headerTextW = pw - rx - mx;   // max width before right margin
    setFont(13, dark, 'bold'); doc.text(agent.company_name, rx, y + 5);
    setFont(8, grey);
    let hy = y + 10;
    if (agent.address) {
      const fullAddr = agent.address + (agent.city ? `, ${agent.city}` : '') + (agent.state ? `, ${agent.state}` : '');
      const addrLines = doc.splitTextToSize(fullAddr, headerTextW);
      addrLines.forEach(line => { doc.text(line, rx, hy); hy += 4; });
    }
    if (agent.phone) { doc.text(agent.phone, rx, hy); hy += 4; }
    if (agent.email) { doc.text(agent.email, rx, hy); hy += 4; }
    if (agent.website) { doc.text(agent.website, rx, hy); hy += 4; }
    // Expand header height to fit wrapped address; keep at least 40mm
    y = Math.max(y + 40, hy + 12);

    // ---- QUOTATION / BOOKING DETAILS HEADING ----
    const bookingMode = !!opts.bookingMode;
    const headingTitle = bookingMode ? 'Booking Details' : 'Quotation Details';
    setFont(18, dark, 'bold'); doc.text(headingTitle, mx, y);
    y += 3;
    drawLine(mx, y, mx + cw, y, teal, 0.8);
    y += 8;

    // ---- TRIP OVERVIEW CARD ----
    const cardY = y;
    drawRect(mx, cardY, cw, 42, [250, 250, 250], 3);
    y += 5;
    setFont(12, dark, 'bold'); doc.text('Trip Overview', mx + 5, y); y += 7;

    const col1 = mx + 6, col2 = mx + 62, col3 = mx + 120;

    if (bookingMode) {
      // Row 1: Booking No | Destination | Travelers
      setFont(8, grey); doc.text('Booking No', col1, y);
      setFont(9, teal, 'bold'); doc.text(`#${opts.bookingNumber || quote.quote_number || quote.id.slice(0, 8)}`, col1, y + 5);
      setFont(8, grey); doc.text('Destination', col2, y);
      setFont(9, dark, 'bold'); doc.text(quote.destination || '-', col2, y + 5);
      setFont(8, grey); doc.text('Travelers', col3, y);
      let travelersText = `${quote.pax_adults || 0} Adults`;
      if ((quote.cwb || 0) > 0) travelersText += `, ${quote.cwb} CWB`;
      if ((quote.cnb || 0) > 0) travelersText += `, ${quote.cnb} CNB`;
      if ((quote.pax_infants || 0) > 0) travelersText += `, ${quote.pax_infants} Infants`;
      setFont(9, dark, 'bold'); doc.text(travelersText, col3, y + 5);
      let agesLine = '';
      if (quote.cwb_ages && String(quote.cwb_ages).length > 0) agesLine += `CWB Ages: ${quote.cwb_ages} yrs`;
      if (quote.cnb_ages && String(quote.cnb_ages).length > 0) agesLine += (agesLine ? '  |  ' : '') + `CNB Ages: ${quote.cnb_ages} yrs`;
      if (agesLine) { setFont(7, grey); doc.text(agesLine, col3, y + 9); }
      y += 14;

      // Row 2: Travel Date | Return Date | Duration
      setFont(8, grey); doc.text('Travel Date', col1, y);
      setFont(9, dark, 'bold'); doc.text(formatDate(quote.travel_date), col1, y + 5);
      setFont(8, grey); doc.text('Return Date', col2, y);
      setFont(9, dark, 'bold'); doc.text(formatDate(quote.return_date), col2, y + 5);
      setFont(8, grey); doc.text('Duration', col3, y);
      setFont(9, dark, 'bold'); doc.text(`${nights}N / ${nights + 1}D`, col3, y + 5);
    } else {
      // Row 1: Quotation No | Destination | Travelers
      setFont(8, grey); doc.text('Quotation No', col1, y);
      setFont(9, teal, 'bold'); doc.text(`#${quote.quote_number || quote.id.slice(0, 8)}`, col1, y + 5);
      setFont(8, grey); doc.text('Destination', col2, y);
      setFont(9, dark, 'bold'); doc.text(quote.destination || '-', col2, y + 5);
      setFont(8, grey); doc.text('Travelers', col3, y);
      let travelersText = `${quote.pax_adults || 0} Adults`;
      if ((quote.cwb || 0) > 0) travelersText += `, ${quote.cwb} CWB`;
      if ((quote.cnb || 0) > 0) travelersText += `, ${quote.cnb} CNB`;
      if ((quote.pax_infants || 0) > 0) travelersText += `, ${quote.pax_infants} Infants`;
      setFont(9, dark, 'bold'); doc.text(travelersText, col3, y + 5);
      let agesLine = '';
      if (quote.cwb_ages && String(quote.cwb_ages).length > 0) agesLine += `CWB Ages: ${quote.cwb_ages} yrs`;
      if (quote.cnb_ages && String(quote.cnb_ages).length > 0) agesLine += (agesLine ? '  |  ' : '') + `CNB Ages: ${quote.cnb_ages} yrs`;
      if (agesLine) { setFont(7, grey); doc.text(agesLine, col3, y + 9); }
      y += 14;

      // Row 2: No of Nights | Travel Date | Return Date
      setFont(8, grey); doc.text('No of Nights', col1, y);
      setFont(9, dark, 'bold'); doc.text(`${nights} Nights`, col1, y + 5);
      setFont(8, grey); doc.text('Travel Date', col2, y);
      setFont(9, dark, 'bold'); doc.text(formatDate(quote.travel_date), col2, y + 5);
      setFont(8, grey); doc.text('Return Date', col3, y);
      setFont(9, dark, 'bold'); doc.text(formatDate(quote.return_date), col3, y + 5);
    }
    y = cardY + 42 + 6;

    // ---- GUEST INFORMATION CARD ----
    checkPage(30);
    const guestCardY = y;
    drawRect(mx, guestCardY, cw, 28, [250, 250, 250], 3);
    y += 5;
    setFont(12, dark, 'bold'); doc.text('Guest Information', mx + 5, y);
    y += 7;
    // Guest Name
    setFont(8, grey); doc.text('Guest Name', col1 + 7, y);
    setFont(9, quote.client_name ? dark : lightGrey, quote.client_name ? 'bold' : 'italic');
    doc.text(quote.client_name || 'Not filled', col1 + 7, y + 5);
    // Guest Mobile
    setFont(8, grey); doc.text('Guest Mobile', col2 + 7, y);
    setFont(9, quote.client_phone ? dark : lightGrey, quote.client_phone ? 'bold' : 'italic');
    doc.text(quote.client_phone || 'Not filled', col2 + 7, y + 5);
    y = guestCardY + 28 + 6;

    // ---- FLIGHT DETAILS CARD ----
    const flightsList = Array.isArray(quote.flight_details) ? quote.flight_details : (quote.flight_details ? [quote.flight_details] : []);
    const validFlights = flightsList.filter(f => f.airline || f.flight_number);
    if (quote.show_flight_details && validFlights.length > 0) {
      const flightCardH = 14 + validFlights.length * 32;
      checkPage(flightCardH + 10);
      const flightCardY = y;
      drawRect(mx, flightCardY, cw, flightCardH, [250, 250, 250], 3);
      y += 5;
      setFont(12, dark, 'bold'); doc.text('Flight Details', mx + 5, y);
      y += 8;

      validFlights.forEach((fd, fIdx) => {
        if (fIdx > 0) { y += 4; drawLine(mx + 10, y - 2, mx + cw - 10, y - 2, [220, 220, 220], 0.3); }
        const isNonStop = (fd.connection_type || 'non_stop') === 'non_stop';

        // Airline & Flight Number & PNR
        const fCol1 = mx + 6;
        setFont(10, dark, 'bold'); doc.text(fd.airline || '', fCol1, y);
        setFont(9, grey); doc.text(fd.flight_number || '', fCol1, y + 5);
        if (fd.pnr) {
          drawRect(fCol1, y + 8, 14, 5, teal, 1);
          setFont(7, [255, 255, 255], 'bold'); doc.text('PNR', fCol1 + 2, y + 12);
          doc.setDrawColor(...teal); doc.setLineWidth(0.3);
          doc.roundedRect(fCol1 + 16, y + 8, 22, 5, 1, 1, 'S');
          setFont(7, teal, 'bold'); doc.text(fd.pnr, fCol1 + 18, y + 12);
        }

        // Departure
        const fCol2 = mx + 55;
        setFont(11, dark, 'bold'); doc.text(fd.departure_city || '', fCol2, y);
        setFont(9, dark, 'bold'); doc.text(`${fd.departure_code || ''}  ${fd.departure_time || ''}`, fCol2, y + 5);
        setFont(7, grey); doc.text(fd.departure_date || '', fCol2, y + 10);

        // Duration & Stops (center)
        const fColMid = mx + 105;
        drawLine(fCol2 + 42, y + 3, fColMid - 5, y + 3, grey, 0.3);
        drawLine(fColMid + 15, y + 3, fColMid + 35, y + 3, grey, 0.3);
        doc.setFillColor(...teal); doc.circle(fColMid + 5, y + 3, 2, 'F');
        if (isNonStop) {
          setFont(7, dark); doc.text(`${fd.duration || ''} : Non Stop`, fColMid - 2, y + 9);
        } else {
          setFont(7, dark); doc.text(`${fd.duration || ''}${fd.stops ? ' : ' + fd.stops + ' stop' : ''}`, fColMid - 2, y + 9);
          if (fd.stop_type) { setFont(6, grey); doc.text(fd.stop_type, fColMid - 2, y + 13); }
          if (fd.stop_location) { setFont(6, grey); doc.text(fd.stop_location, fColMid - 2, y + 16); }
        }

        // Arrival
        const fCol3 = mx + 145;
        setFont(11, dark, 'bold'); doc.text(fd.arrival_city || '', fCol3, y);
        setFont(9, dark, 'bold'); doc.text(`${fd.arrival_time || ''}  ${fd.arrival_code || ''}`, fCol3, y + 5);
        setFont(7, grey); doc.text(fd.arrival_date || '', fCol3, y + 10);

        y += 28;
      });

      y = flightCardY + flightCardH + 6;
    }

    // ---- HOTEL DETAILS CARD ----
    checkPage(30);
    // When multi-options, flatten all hotels across all options with option-group markers
    const isMulti = (accommodationOptions || []).length > 1;
    let hotelGroups = [];
    if (isMulti) {
      hotelGroups = accommodationOptions.map((opt, idx) => ({
        optionIdx: idx,
        label: `OPTION ${idx + 1}`,
        hotels: opt.hotels || []
      }));
    } else {
      const activeOpt = getActiveOption();
      const allHotels = activeOpt.hotels || hotels || [];
      hotelGroups = [{ optionIdx: 0, label: '', hotels: allHotels }];
    }
    const totalHotelCount = hotelGroups.reduce((s, g) => s + g.hotels.length, 0);
    const optionHeaderH = isMulti ? 8 : 0;
    const groupGap = isMulti ? 10 : 0; // Extra gap between option groups
    const nonEmptyGroups = hotelGroups.filter(g => g.hotels.length > 0).length;
    const hotelCardH = totalHotelCount > 0
      ? Math.max(28, 12 + totalHotelCount * 42 + (isMulti ? nonEmptyGroups * optionHeaderH + Math.max(0, nonEmptyGroups - 1) * groupGap + 4 : 0))
      : 28;
    const hotelCardY = y;
    // Keep hotel card background white (no grey fill) — only an outer border via individual hotel cards below.
    y += 5;
    setFont(12, dark, 'bold'); doc.text('Hotel Details', mx + 5, y);
    y += 7;

    if (totalHotelCount > 0) {
      let renderedGroupIdx = 0;
      hotelGroups.forEach((group) => {
        // Skip empty groups (don't print badge for option with no hotels to keep PDF clean)
        if (group.hotels.length === 0) return;
        // Add extra gap before each group after the first
        if (renderedGroupIdx > 0) {
          y += 10;
        }
        renderedGroupIdx += 1;
        // Option badge header
        if (isMulti && group.label) {
          checkPage(10);
          const colorMap = [[219, 234, 254, 29, 78, 216], [209, 250, 229, 4, 120, 87], [254, 243, 199, 180, 83, 9]];
          const [br, bg, bb, fr, fg, fb] = colorMap[group.optionIdx % 3];
          const badgeW = doc.getTextWidth(group.label) + 6;
          doc.setFillColor(br, bg, bb);
          doc.roundedRect(mx + 4, y - 3, badgeW, 5, 1, 1, 'F');
          setFont(7, [fr, fg, fb], 'bold');
          doc.text(group.label, mx + 7, y + 0.5);
          y += 7;
        }
        group.hotels.forEach((hotel, hotelIdx) => {
        const hotelNights = hotel.nights || 1;
        const extras = [];
        if (hotel.extra_bed_toggle && hotel.extra_bed_count > 0) extras.push({ label: 'EB/EM', val: hotel.extra_bed_count });
        if (hotel.cwb_toggle && hotel.cwb_count > 0) extras.push({ label: 'CWB', val: hotel.cwb_count });
        if (hotel.cnb_toggle && hotel.cnb_count > 0) extras.push({ label: 'CNB', val: hotel.cnb_count });
        // Match a booking confirmation to this hotel.
        // Single-option layout: prefer POSITIONAL (hotels[idx] ↔ confirmations[idx]) so duplicate hotel names keep their own conf #.
        // Multi-option: positional doesn't apply, fall back to name/city match.
        const _bookingConfs = opts.bookingConfirmations || [];
        let confirmationNo = '';
        if (!isMulti && _bookingConfs[hotelIdx]?.confirmation_number) {
          confirmationNo = _bookingConfs[hotelIdx].confirmation_number;
        } else {
          const _matchConf = _bookingConfs.find(c => {
            const hn = (hotel.hotel_name || hotel.name || '').toString().toLowerCase().trim();
            const cn = (c.hotel || '').toString().toLowerCase().trim();
            return hn && cn && (hn === cn || hn.includes(cn) || cn.includes(hn));
          });
          confirmationNo = _matchConf ? (_matchConf.confirmation_number || '') : '';
        }

        // Pre-wrap Room Category so long strings don't overflow the column.
        const roomText = hotel.room_type || hotel.room_category || 'Standard';
        setFont(8, dark, 'bold');
        const roomLines = doc.splitTextToSize(String(roomText), 42); // ~42mm column width
        const extraRoomRows = Math.max(0, roomLines.length - 1);
        const cardH = (confirmationNo ? 44 : 38) + extraRoomRows * 4;
        checkPage(cardH + 4);
        const cardY = y;
        doc.setDrawColor(168, 85, 247); doc.setLineWidth(0.8);
        doc.line(mx + 4, cardY, mx + 4, cardY + cardH);
        doc.setDrawColor(230, 230, 230); doc.setLineWidth(0.3);
        doc.roundedRect(mx + 4, cardY, cw - 8, cardH, 1.5, 1.5, 'S');
        setFont(10, dark, 'bold'); doc.text(hotel.hotel_name || hotel.name || '-', mx + 8, cardY + 5);
        setFont(7, grey); doc.text(hotel.city || quote.destination || '', mx + 8, cardY + 9);
        const starText = hotel.hotel_category || quote.hotel_category || '';
        if (starText) {
          const starW = doc.getTextWidth(starText) + 4;
          doc.setFillColor(243, 232, 255); doc.roundedRect(mx + cw - 12 - starW, cardY + 2, starW + 2, 5, 1, 1, 'F');
          setFont(6, [168, 85, 247], 'bold'); doc.text(starText, mx + cw - 12 - starW + 2, cardY + 5.5);
        }
        const row1Y = cardY + 14; const c1 = mx + 8; const c2 = mx + 48; const c3 = mx + 95; const c4 = mx + 132;
        setFont(6, grey); doc.text('No of Rooms', c1, row1Y);
        setFont(8, dark, 'bold'); doc.text(String(hotel.rooms_count || hotel.rooms || quote.no_of_rooms || 1), c1, row1Y + 4);
        setFont(6, grey); doc.text('Room Category', c2, row1Y);
        setFont(8, dark, 'bold');
        roomLines.forEach((line, li) => { doc.text(line, c2, row1Y + 4 + li * 4); });
        setFont(6, grey); doc.text('Meal Plan', c3, row1Y);
        setFont(8, dark, 'bold'); doc.text(mealPlanLabel(hotel.meal_plan || quote.meal_plan), c3, row1Y + 4);
        setFont(6, grey); doc.text('Nights', c4, row1Y);
        setFont(8, dark, 'bold'); doc.text(String(hotelNights), c4, row1Y + 4);
        const row2Y = row1Y + 11 + extraRoomRows * 4;
        setFont(6, grey); doc.text('Check In', c1, row2Y);
        setFont(8, dark, 'bold'); doc.text(formatDate(hotel.check_in), c1, row2Y + 4);
        setFont(6, grey); doc.text('Check Out', c2, row2Y);
        setFont(8, dark, 'bold'); doc.text(formatDate(hotel.check_out), c2, row2Y + 4);
        let ec = c3;
        extras.forEach(ex => {
          setFont(6, grey); doc.text(ex.label, ec, row2Y);
          setFont(8, dark, 'bold'); doc.text(String(ex.val), ec, row2Y + 4);
          ec += 25;
        });
        // Confirmation No: teal pill inline (when a booking confirmation exists for this hotel)
        if (confirmationNo) {
          const row3Y = row2Y + 11;
          setFont(6, grey);
          const lbl = 'Confirmation No:';
          doc.text(lbl, c1, row3Y);
          const lblW = doc.getTextWidth(lbl);
          const pillX = c1 + lblW + 3;
          const cnW = doc.getTextWidth(String(confirmationNo)) + 4;
          doc.setFillColor(236, 253, 245);
          doc.roundedRect(pillX, row3Y - 3, cnW + 2, 5, 1, 1, 'F');
          setFont(7, [6, 95, 70], 'bold');
          doc.text(String(confirmationNo), pillX + 2, row3Y + 0.5);
        }
        y = cardY + cardH + 3;
        });
      });
    } else if (quote.hotel_name) {
      setFont(10, dark, 'bold'); doc.text(quote.hotel_name, mx + 8, y);
      setFont(8, grey); doc.text(`${getHotelStarLabel(quote.hotel_category)} | ${quote.destination || ''}`, mx + 8, y + 4);
      y += 10;
    } else {
      setFont(9, lightGrey, 'italic'); doc.text('No hotel details available', mx + cw / 2, y, { align: 'center' });
      y += 6;
    }
    // Use the actual y (may exceed hotelCardH when confirmation rows grew cards) — do NOT roll back.
    y = Math.max(y, hotelCardY + hotelCardH) + 3;

    // ---- DAY-WISE ITINERARY ----
    if (itinerary && itinerary.length > 0) {
      // Extra breathing room (~50px) above the Day-wise Itinerary heading
      y += 13;
      checkPage(20);
      setFont(18, dark, 'bold'); doc.text('Day-wise Itinerary', mx, y); y += 3;
      drawLine(mx, y, mx + cw, y, teal, 0.8); y += 8;

      itinerary.forEach((day, idx) => {
        checkPage(35);

        // Day & Date + City Name
        const dd = parseDayDate(day.date, quote.travel_date, idx);
        const dateLabel = formatDayDateLabel(dd);
        const cityName = day.city || day.title || '';
        const isLastDay = idx === itinerary.length - 1;
        const headerText = `DAY ${day.day || idx + 1}: ${dateLabel}${cityName && !isLastDay ? ' - ' + cityName.toUpperCase() : ''}`;

        // Solid green banner, only corners rounded, white text
        const bannerH = 9;
        const bannerPadX = 5; // left padding of "D" in the banner
        doc.setFillColor(16, 185, 129);
        doc.roundedRect(mx, y, cw, bannerH, 2, 2, 'F');
        setFont(11, [255, 255, 255], 'bold');
        doc.text(headerText, mx + bannerPadX, y + 6.2);
        y += bannerH + 5;

        // Align all content below with the "D" in "DAY" → use mx + bannerPadX
        const contentX = mx + bannerPadX;
        const contentW = cw - bannerPadX;

        // Activity title + description.
        // Bug fix: previously only the FIRST activity was rendered, so
        // multiple sightseeing entries on the same day silently dropped.
        // Now we loop over all entries and render each one.
        // Entry tickets are split out and shown as a single labeled line
        // (similar to Accommodation / Transfer / Vehicle) instead of
        // separate uppercase activity blocks.
        const allActs = Array.isArray(day.activities) ? day.activities : [];
        const entryTickets = allActs.filter(a => (typeof a === 'object') && (a?.type === 'entry_ticket'));
        const acts = allActs.filter(a => !(typeof a === 'object' && a?.type === 'entry_ticket'));
        const dayLevelDesc = day.description || '';

        if (acts.length === 0 && dayLevelDesc) {
          // No structured activities — fall back to day-level description.
          setFont(9, grey);
          const descLines = doc.splitTextToSize(String(dayLevelDesc), contentW);
          descLines.forEach(line => { checkPage(5); doc.text(line, contentX, y); y += 4.5; });
          y += 2;
        }

        acts.forEach((a, ai) => {
          const actTitle = (typeof a === 'string' ? a : (a?.name || a?.title || '')) || '';
          const actDesc = (typeof a === 'object' ? (a?.description || '') : '') || (ai === 0 ? dayLevelDesc : '');
          if (actTitle) {
            checkPage(8);
            setFont(10, dark, 'bold');
            const titleLines = doc.splitTextToSize(String(actTitle).toUpperCase(), contentW);
            titleLines.forEach(line => { checkPage(5); doc.text(line, contentX, y); y += 5; });
          }
          if (actDesc) {
            setFont(9, grey);
            const descLines = doc.splitTextToSize(String(actDesc), contentW);
            descLines.forEach(line => { checkPage(5); doc.text(line, contentX, y); y += 4.5; });
            y += 2;
          }
        });

        // Accommodation / Transfer / Vehicle — LEFT-ALIGNED VERTICAL LIST (indented to D of DAY)
        const vehicleName = day.vehicle || '';
        const rawXfer = (day.transfer_type || '').toLowerCase();
        const isNoTransfer = rawXfer === 'none' || rawXfer === 'no_transfer' || rawXfer === 'no-transfer';
        const transferLabel = rawXfer === 'sic' || rawXfer === 'seat_in_coach' || rawXfer === 'seat-in-coach'
          ? 'Seat in Coach (SIC)'
          : 'Private';

        if (day.hotel && !isMulti) {
          checkPage(6);
          setFont(9, grey);
          const lbl = 'Accommodation:';
          doc.text(lbl, contentX, y);
          const lw = doc.getTextWidth(lbl);
          setFont(9, dark, 'bold');
          doc.text(String(day.hotel), contentX + lw + 2, y);
          y += 5;
        }

        if (!isNoTransfer) {
          checkPage(6);
          setFont(9, grey); doc.text('Transfer:', contentX, y);
          setFont(9, dark, 'bold'); doc.text(transferLabel, contentX + doc.getTextWidth('Transfer:') + 2, y);
          y += 5;
        }

        if (vehicleName) {
          checkPage(6);
          setFont(9, grey); doc.text('Vehicle:', contentX, y);
          setFont(9, dark, 'bold'); doc.text(String(vehicleName), contentX + doc.getTextWidth('Vehicle:') + 2, y);
          y += 5;
        }

        // Entry Tickets — rendered as a single labeled line, e.g.
        //   Entry Tickets: Universal Studio, Madame Tussauds, Wings of Time
        if (entryTickets.length > 0) {
          const ticketNames = entryTickets
            .map(t => t?.name || t?.title || '')
            .filter(Boolean);
          if (ticketNames.length > 0) {
            const lbl = 'Entry Tickets:';
            const lblW = doc.getTextWidth(lbl);
            const valueX = contentX + lblW + 2;
            const valueW = contentW - lblW - 2;
            setFont(9, dark, 'bold');
            const ticketLines = doc.splitTextToSize(ticketNames.join(', '), valueW);
            ticketLines.forEach((line, li) => {
              checkPage(6);
              if (li === 0) {
                setFont(9, grey); doc.text(lbl, contentX, y);
                setFont(9, dark, 'bold'); doc.text(line, valueX, y);
              } else {
                doc.text(line, valueX, y);
              }
              y += 5;
            });
          }
        }

        y += isLastDay ? 2 : 6;
      });
    }

    // ---- INCLUSIONS & EXCLUSIONS ----
    const halfW = (cw - 6) / 2;

    // Lexend has no ✓/✗/§ glyphs (they render blank), so draw marks as vector lines.
    const drawCheck = (cx, cy, s, color, lw) => {
      doc.setDrawColor(...color); doc.setLineWidth(lw);
      doc.line(cx - s * 0.5, cy, cx - s * 0.15, cy + s * 0.4);
      doc.line(cx - s * 0.15, cy + s * 0.4, cx + s * 0.55, cy - s * 0.45);
    };
    const drawCross = (cx, cy, s, color, lw) => {
      doc.setDrawColor(...color); doc.setLineWidth(lw);
      doc.line(cx - s * 0.4, cy - s * 0.4, cx + s * 0.4, cy + s * 0.4);
      doc.line(cx - s * 0.4, cy + s * 0.4, cx + s * 0.4, cy - s * 0.4);
    };
    // One item per line; strip any bullet chars typed by the user.
    const toItems = (txt) => (txt || '').split('\n')
      .map(s => s.replace(/^\s*[•\-*✓✗]\s*/, '').trim())
      .filter(Boolean);

    // Admin PDF: exactly the quote's own inclusions/exclusions (same as the preview).
    // Agent PDF (opts.agentView): agent-filled add-ons are listed as inclusions,
    // and the matching flight/visa lines are dropped from exclusions.
    const incItems = toItems(quote.inclusions);
    let excItems = toItems(quote.exclusions);
    if (opts.agentView) {
      if (flightPrice > 0) incItems.push('Round-trip flight tickets');
      if (hotelPrice > 0)  incItems.push('Hotel accommodation');
      if (visaPrice > 0)   incItems.push('Visa charges');
      const flightRe = /\b(air\s*-?\s*fares?|flights?|air\s*tickets?|return\s*tickets?)\b/i;
      const visaRe = /\bvisas?\b/i;
      excItems = excItems.filter(it =>
        !(flightPrice > 0 && flightRe.test(it)) && !(visaPrice > 0 && visaRe.test(it))
      );
    }

    // Wrap each item separately so continuation lines stay indented under the text.
    setFont(7, grey);
    const textIndent = 5.5;
    const itemGap = 0.9;
    const incWrapped = incItems.map(it => doc.splitTextToSize(it, halfW - 10 - textIndent));
    const excWrapped = excItems.map(it => doc.splitTextToSize(it, halfW - 10 - textIndent));
    const blockH = (w) => w.reduce((h, l) => h + l.length * 3.5 + itemGap, 0);
    const incHeight = Math.max(25, 16 + blockH(incWrapped) + 4);
    const excHeight = Math.max(25, 16 + blockH(excWrapped) + 4);
    const cardHeight = Math.max(incHeight, excHeight);

    const drawItems = (wrapped, x, startY, isInc) => {
      const color = isInc ? teal : red;
      let iy = startY;
      wrapped.forEach(lines => {
        const cx = x + 1.6, cy = iy - 1.1;
        doc.setDrawColor(...color); doc.setLineWidth(0.3);
        doc.circle(cx, cy, 1.5, 'S');
        if (isInc) drawCheck(cx, cy, 1.4, color, 0.3);
        else drawCross(cx, cy, 1.3, color, 0.3);
        setFont(7, grey);
        lines.forEach(l => { doc.text(l, x + textIndent, iy); iy += 3.5; });
        iy += itemGap;
      });
    };

    checkPage(cardHeight + 8);
    const incExcY = y;

    // Inclusions card
    doc.setDrawColor(...teal); doc.setLineWidth(0.5);
    doc.roundedRect(mx, incExcY, halfW, cardHeight, 2, 2, 'S');
    doc.setFillColor(...teal); doc.roundedRect(mx + 5, incExcY + 4, 5, 5, 1, 1, 'F');
    drawCheck(mx + 7.5, incExcY + 6.5, 2.6, [255, 255, 255], 0.5);
    setFont(11, dark, 'bold'); doc.text('Inclusions', mx + 14, incExcY + 9);
    if (incWrapped.length > 0) {
      drawItems(incWrapped, mx + 5, incExcY + 16, true);
    } else {
      setFont(8, lightGrey, 'italic'); doc.text('No inclusions specified', mx + 5, incExcY + 18);
    }

    // Exclusions card
    const exX = mx + halfW + 6;
    doc.setDrawColor(...red); doc.setLineWidth(0.5);
    doc.roundedRect(exX, incExcY, halfW, cardHeight, 2, 2, 'S');
    doc.setFillColor(...red); doc.roundedRect(exX + 5, incExcY + 4, 5, 5, 1, 1, 'F');
    drawCross(exX + 7.5, incExcY + 6.5, 2.4, [255, 255, 255], 0.5);
    setFont(11, dark, 'bold'); doc.text('Exclusions', exX + 14, incExcY + 9);
    if (excWrapped.length > 0) {
      drawItems(excWrapped, exX + 5, incExcY + 16, false);
    } else {
      setFont(8, lightGrey, 'italic'); doc.text('No exclusions specified', exX + 5, incExcY + 18);
    }
    y = incExcY + cardHeight + 8;

    // ---- TERMS & CONDITIONS ----
    if (quote.show_terms_conditions && quote.terms_conditions) {
      const tcLines = doc.splitTextToSize(quote.terms_conditions, cw - 12);
      const tcHeight = Math.max(25, 16 + tcLines.length * 3.5 + 4);
      checkPage(tcHeight + 8);
      const tcY = y;
      const indigo = [79, 70, 229];
      doc.setDrawColor(...indigo); doc.setLineWidth(0.5);
      doc.roundedRect(mx, tcY, cw, tcHeight, 2, 2, 'S');
      doc.setFillColor(...indigo); doc.roundedRect(mx + 5, tcY + 4, 5, 5, 1, 1, 'F');
      // Document-lines mark (Lexend has no § glyph)
      doc.setDrawColor(255, 255, 255); doc.setLineWidth(0.4);
      doc.line(mx + 6.3, tcY + 5.6, mx + 8.7, tcY + 5.6);
      doc.line(mx + 6.3, tcY + 6.5, mx + 8.7, tcY + 6.5);
      doc.line(mx + 6.3, tcY + 7.4, mx + 7.9, tcY + 7.4);
      setFont(11, dark, 'bold'); doc.text('Terms & Conditions', mx + 14, tcY + 9);
      if (tcLines.length > 0) {
        setFont(7, grey);
        let ty = tcY + 15;
        tcLines.forEach(line => { doc.text(line, mx + 5, ty); ty += 3.5; });
      }
      y = tcY + tcHeight + 8;
    }

    // ---- COST BREAKDOWN (only when withPrice is true) ----
    if (opts.withPrice) {
      // Extra breathing room (~50px) between Inclusions/Exclusions and Cost Breakdown
      y += 13;
      checkPage(35);
      setFont(18, dark, 'bold'); doc.text('Cost Breakdown', mx, y); y += 3;
      drawLine(mx, y, mx + cw, y, teal, 0.8); y += 10;

      const adultCount = quote.pax_adults || 0;
      const cwbCount = quote.cwb || 0;
      const cnbCount = quote.cnb || 0;
      const gstApplicable = !!(quote.gst_required || String(quote.gstin || '').trim());
      // Addons (flight/hotel/visa) are distributed per adult — never shown as separate lines
      const addonsPerAdult = adultCount > 0 ? addonsTotal / adultCount : 0;

      const computeOptionSelling = (opt) => {
        const dmcA = opt.quote_per_adult || 0;
        const dmcC = opt.quote_per_cwb || 0;
        const dmcN = opt.quote_per_cnb || 0;
        const mA = markupMode === 'percentage' ? dmcA * (markupPercentage / 100) : (agentMarkup.adult || 0);
        const mC = markupMode === 'percentage' ? dmcC * (markupPercentage / 100) : (agentMarkup.cwb || 0);
        const mN = markupMode === 'percentage' ? dmcN * (markupPercentage / 100) : (agentMarkup.cnb || 0);
        const gstMult = gstApplicable ? 1.05 : 1;
        // Per adult includes: DMC (×gstMult) + add-ons per adult + markup
        const sA = dmcA * gstMult + addonsPerAdult + mA;
        const sC = dmcC * gstMult + mC;
        const sN = dmcN * gstMult + mN;
        const subtotal = (sA * adultCount) + (sC * cwbCount) + (sN * cnbCount);
        const gstOnSelling = gstApplicable ? subtotal * 0.05 : 0;
        const total = subtotal + gstOnSelling;
        return { sA, sC, sN, subtotal, gstOnSelling, total };
      };

      if (isMulti) {
        accommodationOptions.forEach((opt, optIdx) => {
          checkPage(50);
          const { sA, sC, sN, subtotal, gstOnSelling, total } = computeOptionSelling(opt);
          const colorMap = [[219, 234, 254, 29, 78, 216], [209, 250, 229, 4, 120, 87], [254, 243, 199, 180, 83, 9]];
          const [br, bg, bb, fr, fg, fb] = colorMap[optIdx % 3];
          const label = `OPTION ${optIdx + 1}`;
          const bw = doc.getTextWidth(label) + 8;
          doc.setFillColor(br, bg, bb);
          doc.roundedRect(mx, y - 4, bw, 7, 1.5, 1.5, 'F');
          setFont(9, [fr, fg, fb], 'bold');
          doc.text(label, mx + 4, y + 1);
          y += 8;
          if (sA > 0 && adultCount > 0) { setFont(10, dark, 'bold'); doc.text(`TOTAL PER ADULT COST:  INR ${formatCurrency(sA).replace('₹','')}  x  ${adultCount}`, mx + 5, y); y += 7; }
          if (sC > 0 && cwbCount > 0)   { setFont(10, dark, 'bold'); doc.text(`CWB:  INR ${formatCurrency(sC).replace('₹','')}  x  ${cwbCount}`, mx + 5, y); y += 7; }
          if (sN > 0 && cnbCount > 0)   { setFont(10, dark, 'bold'); doc.text(`CNB:  INR ${formatCurrency(sN).replace('₹','')}  x  ${cnbCount}`, mx + 5, y); y += 7; }
          if (gstApplicable && gstOnSelling > 0) {
            setFont(10, dark, 'bold');
            doc.text(`5% GST:  INR ${formatCurrency(gstOnSelling).replace('₹','')}`, mx + 5, y);
            y += 7;
          }
          drawRect(mx, y - 3, cw, 10, teal, 2);
          setFont(11, [255, 255, 255], 'bold');
          doc.text(`TOTAL COST:  INR ${formatCurrency(total).replace('₹','')}`, mx + 5, y + 3);
          y += 24;
        });
      } else {
        const dmcA = quote.quote_per_adult || 0;
        const dmcC = quote.quote_per_cwb || 0;
        const dmcN = quote.quote_per_cnb || 0;
        const mA = markupMode === 'percentage' ? dmcA * (markupPercentage / 100) : (agentMarkup.adult || 0);
        const mC = markupMode === 'percentage' ? dmcC * (markupPercentage / 100) : (agentMarkup.cwb || 0);
        const mN = markupMode === 'percentage' ? dmcN * (markupPercentage / 100) : (agentMarkup.cnb || 0);
        const gstMult = gstApplicable ? 1.05 : 1;
        const perAdultFinal = dmcA * gstMult + addonsPerAdult + mA;
        const perCwbFinal   = dmcC * gstMult + mC;
        const perCnbFinal   = dmcN * gstMult + mN;
        const sellingSubtotal = (perAdultFinal * adultCount) + (perCwbFinal * cwbCount) + (perCnbFinal * cnbCount);
        const gstAmount = gstApplicable ? sellingSubtotal * 0.05 : 0;
        const totalCost = sellingSubtotal + gstAmount;

        // Line 1: TOTAL PER ADULT COST (always shown)
        if (perAdultFinal > 0 && adultCount > 0) { setFont(10, dark, 'bold'); doc.text(`TOTAL PER ADULT COST:  INR ${formatCurrency(perAdultFinal).replace('₹','')}  x  ${adultCount}`, mx + 5, y); y += 7; }
        if (perCwbFinal > 0 && cwbCount > 0)     { setFont(10, dark, 'bold'); doc.text(`CWB:  INR ${formatCurrency(perCwbFinal).replace('₹','')}  x  ${cwbCount}`, mx + 5, y); y += 7; }
        if (perCnbFinal > 0 && cnbCount > 0)     { setFont(10, dark, 'bold'); doc.text(`CNB:  INR ${formatCurrency(perCnbFinal).replace('₹','')}  x  ${cnbCount}`, mx + 5, y); y += 7; }

        // Line 2 (GST ON only): 5% GST below per adult, above total bar
        if (gstApplicable && gstAmount > 0) {
          setFont(10, dark, 'bold');
          doc.text(`5% GST:  INR ${formatCurrency(gstAmount).replace('₹','')}`, mx + 5, y);
          y += 10;
        } else {
          y += 3;
        }

        // Final line: TOTAL COST (green bar)
        checkPage(18);
        drawRect(mx, y - 3, cw, 12, teal, 2);
        setFont(12, [255, 255, 255], 'bold');
        doc.text(`TOTAL COST:  INR ${formatCurrency(totalCost).replace('₹','')}`, mx + 5, y + 4);
        y += 21;
      }
    }

    // ---- AGENT SIGNATURE BLOCK ----
    checkPage(40);
    y += 6;
    const sigY = y;
    const sigH = 32;
    drawRect(mx, sigY, cw, sigH, [249, 250, 251], 3);
    // Label
    setFont(8, grey, 'bold'); doc.text('YOUR TRAVEL ADVISOR', mx + 6, sigY + 6);
    // Profile picture (circle on the left)
    const picSize = 20;
    const picX = mx + 6;
    const picY = sigY + 9;
    if (agent.profile_picture_url) {
      try {
        const ext = (agent.profile_picture_url.startsWith('data:image/jpeg') || agent.profile_picture_url.startsWith('data:image/jpg')) ? 'JPEG' : 'PNG';
        // jsPDF's addImage always draws a plain rectangle — clip to a circular
        // path first so the photo actually matches the circular placeholder/badge.
        doc.saveGraphicsState();
        doc.circle(picX + picSize / 2, picY + picSize / 2, picSize / 2, null);
        doc.clip();
        doc.discardPath();
        doc.addImage(agent.profile_picture_url, ext, picX, picY, picSize, picSize, undefined, 'FAST');
        doc.restoreGraphicsState();
      } catch (err) {
        console.warn('PDF profile pic embed failed:', err);
        drawRect(picX, picY, picSize, picSize, [230, 230, 230], 10);
      }
    } else {
      // Placeholder with initials
      drawRect(picX, picY, picSize, picSize, teal, 10);
      const initials = (agent.name || 'A').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
      setFont(10, [255, 255, 255], 'bold'); doc.text(initials, picX + picSize / 2, picY + picSize / 2 + 2, { align: 'center' });
    }
    // Text block
    const txX = picX + picSize + 6;
    if (agent.name) { setFont(11, dark, 'bold'); doc.text(agent.name, txX, sigY + 14); }
    if (agent.designation) { setFont(8, grey); doc.text(agent.designation, txX, sigY + 19); }
    if (agent.phone) { setFont(8, dark); doc.text(`Mobile: ${agent.phone}`, txX, sigY + 24); }
    if (agent.email) { setFont(8, grey); doc.text(agent.email, txX, sigY + 28); }
    y = sigY + sigH + 4;

    // Footer — use local Date directly to avoid UTC offset showing wrong day
    const _now = new Date();
    const _footerDate = `${String(_now.getDate()).padStart(2,'0')}/${String(_now.getMonth()+1).padStart(2,'0')}/${_now.getFullYear()}`;
    setFont(7, lightGrey);
    doc.text(`Generated on ${_footerDate} | ${agent.company_name}`, pw / 2, 290, { align: 'center' });


  return doc;
}

export default generateQuotePdfDoc;
